import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useForm } from '@inertiajs/react'
import { FormEvent } from 'react'

interface Community {
  id: number
  name: string
}

interface Contest {
  id: number
  title: string
  description: string
  theme: string
  community_id: number
  submission_start_date: string
  submission_end_date: string
  voting_end_date: string
  participation_points: number
  vote_points: number
  first_place_points: number
  second_place_points: number
  third_place_points: number
}

interface EditProps {
  contest: Contest
  communities: Community[]
}

export default function Edit({ contest, communities }: EditProps) {
  const { data, setData, put, processing, errors } = useForm({
    title: contest.title,
    description: contest.description || '',
    theme: contest.theme || '',
    community_id: contest.community_id.toString(),
    submission_start_date: contest.submission_start_date.slice(0, 16),
    submission_end_date: contest.submission_end_date.slice(0, 16),
    voting_end_date: contest.voting_end_date.slice(0, 16),
    participation_points: contest.participation_points,
    vote_points: contest.vote_points,
    first_place_points: contest.first_place_points,
    second_place_points: contest.second_place_points,
    third_place_points: contest.third_place_points
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    put(`/admin/photo_contests/${contest.id}`)
  }

  return (
    <AdminLayout
      title="Modifier Concours"
      breadcrumbs={[
        { label: 'Concours Photo', href: '/admin/photo_contests' },
        { label: contest.title, href: `/admin/photo_contests/${contest.id}` },
        { label: 'Modifier' }
      ]}
    >
      <div className="max-w-3xl">
        <form onSubmit={handleSubmit}>
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">Titre *</label>
                <Input
                  value={data.title}
                  onChange={(e) => setData('title', e.target.value)}
                  required
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">Thème</label>
                <Input
                  value={data.theme}
                  onChange={(e) => setData('theme', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">Description</label>
                <Textarea
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                  rows={4}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">Communauté *</label>
                <Select
                  value={data.community_id}
                  onChange={(e) => setData('community_id', e.target.value)}
                  required
                >
                  {communities.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-dark mb-1">
                    Début soumissions *
                  </label>
                  <Input
                    type="datetime-local"
                    value={data.submission_start_date}
                    onChange={(e) => setData('submission_start_date', e.target.value)}
                    required
                  />
                  {errors.submission_start_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.submission_start_date}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-dark mb-1">
                    Fin soumissions *
                  </label>
                  <Input
                    type="datetime-local"
                    value={data.submission_end_date}
                    onChange={(e) => setData('submission_end_date', e.target.value)}
                    required
                  />
                  {errors.submission_end_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.submission_end_date}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-dark mb-1">
                    Fin votes *
                  </label>
                  <Input
                    type="datetime-local"
                    value={data.voting_end_date}
                    onChange={(e) => setData('voting_end_date', e.target.value)}
                    required
                  />
                  {errors.voting_end_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.voting_end_date}</p>
                  )}
                  <p className="mt-1 text-xs text-text-gray">
                    Doit être après la fin des soumissions
                  </p>
                </div>
              </div>

              <div>
              <h3 className="text-sm font-semibold text-text-dark mb-3">Nombre de points attribués</h3>
              <div className="grid grid-cols-5 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    Participation
                  </label>
                  <Input
                    type="number"
                    value={data.participation_points}
                    onChange={(e) => setData('participation_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    Par vote
                  </label>
                  <Input
                    type="number"
                    value={data.vote_points}
                    onChange={(e) => setData('vote_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    1ère place
                  </label>
                  <Input
                    type="number"
                    value={data.first_place_points}
                    onChange={(e) => setData('first_place_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    2ème place
                  </label>
                  <Input
                    type="number"
                    value={data.second_place_points}
                    onChange={(e) => setData('second_place_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    3ème place
                  </label>
                  <Input
                    type="number"
                    value={data.third_place_points}
                    onChange={(e) => setData('third_place_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
              </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-4">
              <Button type="button" variant="secondary" onClick={() => history.back()}>
                Annuler
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Mise à jour...' : 'Mettre à jour'}
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </AdminLayout>
  )
}
